import { useQuery } from "@tanstack/react-query";
import { API_URL } from "../../../core/constants";
import { getJupiterToken } from "../../../core/cookie";
import { Spinner } from "@material-tailwind/react";
import { useState } from "react";

const UserList = () => {
  const [unauthorized, setUnauthorized] = useState(false);

  const { isPending, error, data } = useQuery({
    queryKey: ["users"],
    queryFn: async () => {
      const url = `${API_URL}/jupiter/users`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-jupiter-token": getJupiterToken(),
        },
      });

      if (response.status === 401) {
        console.error("Unauthorized access");
        setUnauthorized(true);
        return;
      }

      return response.json();
    },
  });

  const renderLoading = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderError = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <div className="w-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
              />
            </svg>
          </div>
          <span>An error has occurred, please try again.</span>
        </div>
      </div>
    );
  };

  const renderNotAuthorised = () => {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="w-16 h-16 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M256 0c-25.3 0-47.2 14.7-57.6 36c-7-2.6-14.5-4-22.4-4c-35.3 0-64 28.7-64 64l0 165.5-2.7-2.7c-25-25-65.5-25-90.5 0s-25 65.5 0 90.5L106.5 437c48 48 113.1 75 181 75l8.5 0 8 0c1.5 0 3-.1 4.5-.4c91.7-6.2 165-79.4 171.1-171.1c.3-1.5 .4-3 .4-4.5l0-176c0-35.3-28.7-64-64-64c-5.5 0-10.9 .7-16 2l0-2c0-35.3-28.7-64-64-64c-7.9 0-15.4 1.4-22.4 4C303.2 14.7 281.3 0 256 0zM240 96.1l0-.1 0-32c0-8.8 7.2-16 16-16s16 7.2 16 16l0 31.9 0 .1 0 136c0 13.3 10.7 24 24 24s24-10.7 24-24l0-136c0 0 0 0 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 55.9c0 0 0 .1 0 .1l0 80c0 13.3 10.7 24 24 24s24-10.7 24-24l0-71.9c0 0 0-.1 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 172.9c-.1 .6-.1 1.3-.2 1.9c-3.4 69.7-59.3 125.6-129 129c-.6 0-1.3 .1-1.9 .2l-4.9 0-8.5 0c-55.2 0-108.1-21.9-147.1-60.9L52.7 315.3c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L119 336.4c6.9 6.9 17.2 8.9 26.2 5.2s14.8-12.5 14.8-22.2L160 96c0-8.8 7.2-16 16-16c8.8 0 16 7.1 16 15.9L192 232c0 13.3 10.7 24 24 24s24-10.7 24-24l0-135.9z" />
          </svg>
        </div>
        <div className="m-auto justify-center items-center flex flex-col gap-6 text-base">
          <span>You cannot view this. You are not authorised.</span>
        </div>
      </div>
    );
  };

  if (isPending) {
    return renderLoading();
  }
  if (error) {
    return renderError();
  }
  if (unauthorized) {
    return renderNotAuthorised();
  }

  return (
    <section>
      <table className="w-full">
        <thead className="border-b border-slate-200 bg-slate-100 text-sm font-medium text-slate-600 dark:bg-slate-900">
          <tr>
            <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
              <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                Email
              </small>
            </th>
            <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
              <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                Role
              </small>
            </th>
            <th className="cursor-pointer px-2.5 py-2 text-start font-medium">
              <small className="font-sans antialiased text-sm text-current flex items-center justify-between gap-2 opacity-70">
                Secret
              </small>
            </th>
          </tr>
        </thead>
        {data?.map((user) => (
          <tbody
            key={user._id}
            className="border-b border-slate-200 bg-white text-sm font-normal text-slate-600 dark:bg-slate-900 dark:text-slate-400"
          >
            <tr className="hover:bg-slate-100 dark:hover:bg-slate-800">
              <td className="cursor-pointer px-2.5 py-2 text-start font-medium">
                {user.email}
              </td>
              <td className="cursor-pointer px-2.5 py-2 text-start font-medium">
                {user.role}
              </td>
              <td className="cursor-pointer px-2.5 py-2 text-start font-medium font-mono text-xs">
                {user.sharedSecret}
              </td>
            </tr>
          </tbody>
        ))}
      </table>
    </section>
  );
};

export default UserList;
